import { ReadSyncableStateStatisticsDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import { usePolling } from 'modules/integrations/hooks/usePolling'

const SYNCING_POLLING_INTERVAL_MS = 2 * 1000
const IDLE_POLLING_INTERVAL_MS = 3 * 60 * 1000

export const useSyncableStateStatistics = ({
  skip = false,
  isSyncing = false,
}: { skip?: boolean; isSyncing?: boolean } = {}) => {
  const {
    syncEnabled,
    marketplaceIntegration,
    configuration,
    dataCollectionBaseGraphqlJsonString,
    companyId,
    contextParameters,
    isBookkeeperView,
  } = usePrivateIntegrationsSyncContext()

  const pollingEnabled = configuration.pollingEnabled ?? false

  const [fetchSyncableStateStatistics, { data, loading }] = useLazyQuery(
    ReadSyncableStateStatisticsDocument
  )

  const serializedContextParameters = JSON.stringify(contextParameters)

  const canFetch = !skip && !!syncEnabled && !!marketplaceIntegration && !!companyId

  useUnsafeEffect(() => {
    if (!canFetch) return

    fetchSyncableStateStatistics({
      variables: {
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson: dataCollectionBaseGraphqlJsonString,
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    canFetch,
    marketplaceIntegration,
    companyId,
    contextParameters,
    isBookkeeperView,
    configuration.origin,
    serializedContextParameters,
    dataCollectionBaseGraphqlJsonString,
    fetchSyncableStateStatistics,
  ])

  const pollCallback = () => {
    if (!companyId || !marketplaceIntegration) return

    fetchSyncableStateStatistics({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
        graphqlVariablesJson: dataCollectionBaseGraphqlJsonString,
      },
      context: {
        isBookkeeperView,
      },
      fetchPolicy: 'network-only',
    })
  }

  usePolling({
    enabled: pollingEnabled && !!syncEnabled && !skip && !!companyId,
    callback: pollCallback,
    intervalMs: isSyncing ? SYNCING_POLLING_INTERVAL_MS : IDLE_POLLING_INTERVAL_MS,
  })

  const statistics = data?.integrations?.syncableStateStatisticsConnection?.nodes?.[0]
  const resolvedStatistics = canFetch ? statistics : null

  return { statistics: resolvedStatistics, loading }
}
